/** Offset of a zone at a given instant, in minutes east of UTC. */
export function zoneOffsetMinutes(instant: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(instant);

  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

export interface DstTransition {
  at: Date;
  /** true when clocks move forward (summer time starts). */
  forward: boolean;
  fromOffset: number;
  toOffset: number;
}

/**
 * Upcoming DST transitions for a zone.
 *
 * Scans day by day for an offset change, then narrows to the minute — good
 * enough to tell an operator when the clocks move, without pulling in a
 * timezone database.
 */
export function nextDstTransitions(timezone: string, count = 2, from = new Date()): DstTransition[] {
  const result: DstTransition[] = [];
  const DAY = 24 * 60 * 60 * 1000;

  let cursor = from;
  let offset = zoneOffsetMinutes(cursor, timezone);

  for (let day = 0; day < 800 && result.length < count; day++) {
    const next = new Date(cursor.getTime() + DAY);
    const nextOffset = zoneOffsetMinutes(next, timezone);

    if (nextOffset !== offset) {
      // Narrow the change down to the minute.
      let lo = cursor.getTime();
      let hi = next.getTime();
      while (hi - lo > 60_000) {
        const mid = lo + Math.floor((hi - lo) / 2);
        if (zoneOffsetMinutes(new Date(mid), timezone) === offset) lo = mid; else hi = mid;
      }
      result.push({
        at: new Date(hi),
        forward: nextOffset > offset,
        fromOffset: offset,
        toOffset: nextOffset,
      });
      offset = nextOffset;
    }

    cursor = next;
  }

  return result;
}

/** "UTC+3" / "UTC-4:30" */
export function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
}
